import PersonPinRounded from '@mui/icons-material/PersonPinRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useState } from 'react';

import { Piece, RecipientAutoDetectionStatus } from '@apiContract';
import { useAssignmentRecipient } from '@components/Capture/hooks/useAssignmentRecipient';
import { useUpdateQueue } from '@components/Capture/hooks/useUpdateQueue';
import { Iconify } from '@components/Iconify';
import { useSettings } from '@hooks/useSettings';
import { useUserInfoLines } from 'src/hooks/useUserInfoLines';

import { getRecipientFullName } from '@utils/recipient';

import {
  mdNotification,
  scrollContainer,
  sxIcon,
  sxNotification,
  sxNotificationAction,
  sxNotificationText,
} from './styles';

type Props = {
  piece?: Piece | null;
};

type Settings = {
  AssignmentCardShowMiddleName: boolean;
  AssignmentCardShowAutoAssignId: boolean;
};

type LineComponentProps = {
  icon: string;
  line?: string;
};

const LineComponent = ({ icon, line }: LineComponentProps) => {
  const theme = useTheme();

  if (!line) return;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textWrap: 'nowrap' }}>
      <Iconify icon={icon} width={16} height={16} color={theme.palette.text.secondary} sx={{ m: '0 4px 0 0' }} />
      <Typography variant="caption" sx={{ color: 'grey.500' }}>
        {line}
      </Typography>
    </Box>
  );
};

const RecipientNotification: FC<Props> = ({ piece }) => {
  const { AssignmentCardShowMiddleName: showMiddleName, AssignmentCardShowAutoAssignId: showAutoAssignId } =
    useSettings(['AssignmentCardShowMiddleName', 'AssignmentCardShowAutoAssignId']) as Settings;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { addEvent } = useUpdateQueue({ context: 'MailPiece' });
  const { assignRecipientToPiece } = useAssignmentRecipient(addEvent);
  const isGroup = !!piece?.recipient?.groupId;
  const pieceRecipient = piece?.recipient || piece?.recipientAutoDetection?.recipient;
  const recipientFullName = isGroup
    ? piece?.recipient?.displayName || ''
    : getRecipientFullName(pieceRecipient, showMiddleName);
  const {
    idLine: userIdLine,
    routesLine,
    isRoutesLoading,
    isLoadingGroups,
    userGroups,
    showGroups,
    lockerLine,
    isLoadingLocker,
    isShowLockerLine,
    pieceLocations,
    isLoadingLocationPlacement,
    isLocationPlacementEnabled,
  } = useUserInfoLines(isGroup ? undefined : pieceRecipient?.user, { showLocker: true, showPieceLocations: true });

  const idLine = showAutoAssignId && piece?.recipient?.group ? piece.recipient.group.autoAssignId : userIdLine;

  const pieceStatus = piece?.recipientAutoDetection?.status;

  const isConfirm =
    !piece?.recipient && pieceRecipient && pieceStatus === RecipientAutoDetectionStatus.IdentifiedVerificationRequired;

  const onConfirm = () => {
    setIsLoading(true);
    if (isConfirm && piece) {
      assignRecipientToPiece(pieceRecipient, piece);
    }
  };

  useEffect(() => {
    if (pieceRecipient || isGroup) {
      setIsVisible(true);
      setIsLoading(false);
    }
  }, [pieceRecipient, isGroup]);

  const onClose = () => {
    setIsVisible(false);
  };

  const color = isConfirm ? 'warning' : 'success';

  const showUsersGroupInfo = !!pieceRecipient?.user?.groupIds?.length && showGroups && !isGroup;

  return (
    isVisible && (
      <Box sx={isMobile ? sxNotification : mdNotification}>
        <Box sx={scrollContainer} className="customScrollBar">
          <Box sx={sxIcon(color)}>
            <PersonPinRounded color={color} />
          </Box>
          <Box sx={sxNotificationText}>
            <Typography variant="subtitle2">{recipientFullName} </Typography>
            <Box>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                {idLine}
              </Typography>
            </Box>
            {showUsersGroupInfo &&
              (isLoadingGroups ? (
                <Skeleton variant="text" width="40%" />
              ) : (
                <LineComponent icon="material-symbols:groups" line={userGroups?.sort().join(', ')} />
              ))}
            {routesLine &&
              (isRoutesLoading ? (
                <Skeleton variant="text" width="40%" />
              ) : (
                <LineComponent icon="material-symbols:mode-of-travel" line={routesLine} />
              ))}
            {isShowLockerLine &&
              (isLoadingLocker ? (
                <Skeleton variant="text" width="40%" />
              ) : (
                <LineComponent icon="material-symbols:grid-on" line={lockerLine || ''} />
              ))}
            {isLocationPlacementEnabled &&
              (isLoadingLocationPlacement ? (
                <Skeleton variant="text" width="40%" />
              ) : (
                <LineComponent icon="material-symbols:person-pin-circle" line={pieceLocations || ''} />
              ))}
          </Box>
          <Box sx={sxNotificationAction}>
            {isConfirm ? (
              <LoadingButton
                variant="soft"
                size="small"
                color="warning"
                onClick={onConfirm}
                loading={isLoading}
                sx={{ mr: 1, ml: 1 }}
              >
                Confirm
              </LoadingButton>
            ) : (
              <IconButton size="small" onClick={onClose} sx={{ p: 0.5, mr: 0.5 }} aria-label="Dismiss notification">
                <Iconify width={16} icon="mingcute:close-line" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};

export default RecipientNotification;
